import { NotSignedInError, getAccessToken, redirectToLogin, resolveSession, signOut } from "../utils/auth-client";
import { makeReducer } from "./utils";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import {
  FetchError,
  RefreshUnavailableFetchError,
  SignedOutError,
  UsersLambdaFetch,
  awaitJSON,
} from "../utils/fetch-utils";
import { useSetAreYouSure } from "../redux/modals";
import * as R from "ramda";
import * as Sentry from "@sentry/browser";

// Check for a new version of the app every half-hour
const VERSION_CHECK_INTERVAL = 1000 * 60 * 30;
const PAGE_TIMEOUT = 10000; // 10 seconds
const ROOT = "user";

const SET_USER_INFO = `${ROOT}/SET_USER_INFO`;
const SET_IS_AUTHED = `${ROOT}/SET_IS_AUTHED`;
const SET_COMMIT = `${ROOT}/SET_COMMIT`;
const SET_UP_TO_DATE = `${ROOT}/SET_UP_TO_DATE`;
const SET_UP_TO_DATE_DISMISSED = `${ROOT}/SET_UP_TO_DATE_DISMISSED`;

// Why the app isn't showing signed-in content once boot has finished without a session.
export const BootOutcome = {
  SIGNED_IN: "signed_in",
  // The browser is navigating to the sign-in page.
  REDIRECTING: "redirecting",
  // The sign-in page is this page's own '/', so a redirect would loop: show a link instead.
  SIGN_IN_LINK: "sign_in_link",
  // The sign-in service didn't answer: the session may still be good, so never redirect.
  UNAVAILABLE: "unavailable",
};

export const agenciesSelector = R.path([ROOT, "agencies"]);
export const appUpToDateDismissedSelector = R.path([ROOT, "appUpToDateDismissed"]);
export const appUpToDateSelector = R.path([ROOT, "appUpToDate"]);
export const commitHashesSelector = R.path([ROOT, "commitHashes"]);
export const companiesSelector = R.path([ROOT, "companies"]);
export const emailSelector = R.path([ROOT, "email"]);
export const fullNameSelector = R.path([ROOT, "fullName"]);
export const isAdminSelector = R.path([ROOT, "isAdmin"]);
export const isAuthedSelector = R.path([ROOT, "isAuthed"]);
export const authFailureSelector = R.path([ROOT, "authFailure"]);
export const isInternalSelector = R.path([ROOT, "isInternal"]);
export const isNOCSelector = user =>
  R.defaultTo(10, R.path([ROOT, "companies", 0, "role"], user)) <= 3;
export const roleSelector = R.path([ROOT, "companies", 0, "role"]);
export const trueRoleSelector = R.path([ROOT, "trueRole"]);
export const sendingEmailSelector = R.pipe(emailSelector, R.identity);
export const startTimeSelector = R.path([ROOT, "startTime"]);
export const userFetchedSelector = R.path([ROOT, "userFetched"]);
export const userExperimentsSelector = R.path([ROOT, "experiments"]);

const redirectOrLink = () => (redirectToLogin() ? BootOutcome.REDIRECTING : BootOutcome.SIGN_IN_LINK);

const isAuthRejection = e =>
  e instanceof NotSignedInError ||
  (e instanceof FetchError && (e.status === 401 || e.status === 403));

// Resolves where the session comes from and, when there is one, loads the user. Never throws.
export const bootCheck = async () => {
  const source = await resolveSession();
  if (source === "none") {
    return { outcome: redirectOrLink() };
  }
  if (source === "unavailable") {
    return { outcome: BootOutcome.UNAVAILABLE };
  }
  try {
    const userRes = await UsersLambdaFetch("/me", { retries: 0 });
    const userInfo = await awaitJSON(userRes);
    return { outcome: BootOutcome.SIGNED_IN, userInfo };
  } catch (error) {
    // fetch-utils already navigated (or declined to) for a gone session: don't navigate twice.
    if (error instanceof SignedOutError) {
      return { outcome: error.redirected ? BootOutcome.REDIRECTING : BootOutcome.SIGN_IN_LINK, error };
    }
    // /me answered 401 but the refresh that could have recovered it never got an answer: the
    // session may well be good, so this is an outage and must not redirect.
    if (error instanceof RefreshUnavailableFetchError) {
      return { outcome: BootOutcome.UNAVAILABLE, error };
    }
    if (isAuthRejection(error)) {
      return { outcome: redirectOrLink(), error };
    }
    return { outcome: BootOutcome.UNAVAILABLE, error };
  }
};

// The credentials prompt's "Refresh": mint a fresh token, then reload so boot runs again. A
// session that turns out to be gone goes to sign-in; anything else just reloads.
export const refreshCredentialsAndReload = async () => {
  try {
    await getAccessToken({ forceRefresh: true });
  } catch (e) {
    if (e instanceof NotSignedInError && redirectToLogin()) {
      return;
    }
  }
  window.location.reload();
};

export const CREDENTIALS_PROMPT = {
  title: "Refresh Credentials",
  message:
    "We are having trouble verifying your login credentials, you can try refreshing the page or signing out and signing in again!",
  okayText: "Refresh",
  cancelText: "Sign Out",
  variant: "warning",
  onCancel: () => {
    signOut();
  },
  onOkay: refreshCredentialsAndReload,
};

export const useIsAuthed = () => {
  const dispatch = useDispatch();
  const isAuthed = useSelector(isAuthedSelector);
  const setAreYouSure = useSetAreYouSure(true);

  const isBooting = useRef(false);
  const nilIsAuthedTime = useMemo(() => (R.isNil(isAuthed) ? Date.now() : null), [isAuthed]);
  const [now, setNow] = useState(Date.now());

  const promptRefreshCredentials = useCallback(
    async reason => {
      Sentry.addBreadcrumb({
        category: "auth",
        message: `Prompting for credential refresh: ${reason}`,
        level: "info",
      });
      await setAreYouSure(CREDENTIALS_PROMPT);
    },
    [setAreYouSure]
  );

  useEffect(() => {
    // If we have been inactive for over 10 seconds ask the user if they want to refresh the page or try
    // sign in again
    if (!R.isNil(nilIsAuthedTime)) {
      if (now - nilIsAuthedTime > PAGE_TIMEOUT) {
        Sentry.captureMessage("Credential verification timed out, prompting user to refresh", "warning");
        promptRefreshCredentials("timeout");
      } else {
        setTimeout(() => {
          setNow(Date.now());
        }, 1000);
      }
    }
  }, [nilIsAuthedTime, now, promptRefreshCredentials]);

  useEffect(() => {
    // If this is null, that means we haven't checked authentication yet.
    if (!R.isNil(isAuthed) || isBooting.current) {
      return;
    }
    isBooting.current = true;
    (async () => {
      const { outcome, userInfo, error } = await bootCheck();
      if (outcome === BootOutcome.SIGNED_IN) {
        dispatch({ type: SET_USER_INFO, userInfo });
        Sentry.configureScope(scope => {
          scope.setUser({ email: userInfo.email, user: userInfo.email });
        });
        return;
      }
      if (error) {
        console.error(error);
        Sentry.captureMessage(
          outcome === BootOutcome.UNAVAILABLE
            ? "/me failed with network error, prompting user to refresh"
            : "/me rejected auth token, redirecting to login",
          "warning"
        );
      }
      dispatch({ type: SET_IS_AUTHED, isAuthed: false, authFailure: outcome });
      if (outcome === BootOutcome.UNAVAILABLE) {
        await promptRefreshCredentials("sign-in service did not answer");
      }
    })();
  }, [isAuthed, dispatch, promptRefreshCredentials]);

  return isAuthed;
};

const fetchLatestCommits = async () => {
  let latestCommitRes = await UsersLambdaFetch("/latest_commit", {
    retries: 10,
    params: {
      repo: "bpm-frontend",
    },
  });
  const frontendHash = await awaitJSON(latestCommitRes);
  return {
    frontend: frontendHash.hash,
  };
};

export const useVersionCheck = () => {
  const dispatch = useDispatch();
  const commitHashes = useSelector(commitHashesSelector);
  const isAuthed = useSelector(isAuthedSelector);

  const makeVersionCheckInterval = useCallback(
    hashes => {
      let newFrontend = false;
      const int = setInterval(async () => {
        try {
          const latestHashes = await fetchLatestCommits();
          if (!newFrontend && hashes.frontend !== latestHashes.frontend) {
            console.warn("Newer version of the app available");
            dispatch({
              type: SET_UP_TO_DATE,
              appUpToDate: false,
            });
            newFrontend = true;
          }
          if (newFrontend) {
            clearInterval(int);
          }
        } catch (e) {
          console.error("Failed to fetch latest commits after several retries. Giving up.");
          dispatch({
            type: SET_UP_TO_DATE,
            appUpToDate: false,
          });
          clearInterval(int);
        }
      }, VERSION_CHECK_INTERVAL);
      return int;
    },
    [dispatch]
  );

  useEffect(() => {
    if (isAuthed && !commitHashes) {
      (async () => {
        try {
          let hashes = await fetchLatestCommits();

          Sentry.configureScope(scope => {
            scope.setExtra("commitHash", hashes.frontend);
          });
          // TODO: do we need this in redux? We may actually never use it
          dispatch({
            type: SET_COMMIT,
            commitHashes: hashes,
          });
          if (process.env.NODE_ENV !== "development") {
            makeVersionCheckInterval(hashes);
          }
        } catch (e) {
          const message = `Failed to get latest commit hash on first try. Error: ${e.message}`;
          console.error(message);
          Sentry.captureException(message);
        }
      })();
    }
  }, [isAuthed, commitHashes, dispatch, makeVersionCheckInterval]);
};

export const useUpToDate = () => {
  const dispatch = useDispatch();
  const appUpToDate = useSelector(appUpToDateSelector);
  const dismissed = useSelector(appUpToDateDismissedSelector);

  const dismiss = useCallback(() => {
    dispatch({
      type: SET_UP_TO_DATE_DISMISSED,
      appUpToDateDismissed: true,
    });
  }, [dispatch]);

  return {
    appUpToDate: dismissed || appUpToDate,
    dismiss,
  };
};

const setIsAuthed = (state, { isAuthed, authFailure }) => ({
  ...state,
  isAuthed,
  authFailure,
});

const setUserInfo = (state, { userInfo }) => ({
  ...state,
  ...userInfo,
  isAuthed: true,
  userFetched: true,
});

const setCommit = (state, { hash, repo }) => ({
  ...state,
  commitHashes: {
    ...state.commitHashes,
    [repo]: hash,
  },
});
const setUpToDate = (state, { appUpToDate }) => ({ ...state, appUpToDate });
const setUpToDateDismiss = (state, { appUpToDateDismissed }) => ({
  ...state,
  appUpToDateDismissed,
});

export default makeReducer(
  {
    isAdmin: false,
    isInternal: false,
    email: "",
    userFetched: false,
    appUpToDate: true,
    appUpToDateDismissed: false,
    startTime: Date.now(),
  },
  {
    [SET_IS_AUTHED]: setIsAuthed,
    [SET_USER_INFO]: setUserInfo,
    [SET_COMMIT]: setCommit,
    [SET_UP_TO_DATE]: setUpToDate,
    [SET_UP_TO_DATE_DISMISSED]: setUpToDateDismiss,
  }
);
